import React, { useState } from 'react'
import { Box, Divider, Typography, useTheme, TextField, Button, Collapse, Card, CardContent, IconButton, Tooltip, CircularProgress } from '@mui/material'
import addFileImg from '../../../assets/material-upload.svg'
import FileUploadIcon from '@mui/icons-material/FileUpload'
import { Icons, attachmentThumbnails, getFileSizeFromBytes } from '../../../utils/utilities'
import { toast } from 'react-toastify'
import api from '../../../service/api'
import { useParams } from 'react-router-dom'
import { useLiveSessionStore } from '../../../store/livesessions.store'
import { observer } from 'mobx-react'
import { Close } from '@mui/icons-material'
import CustomChip from '../../common/CustomChip'

const supportedFileFormats = [".mp4", ".webp", ".webm", '.pdf', '.docx', '.ppsx', '.pptx', '.odp', '.potx', '.xslx']

const getFileType = ( file ) => {
    const mime = file.type
    const [type] = mime.split( "/" )
    if ( type === 'video' || mime === "application/x-mpegURL" ) return "video"
    if ( type === 'image' ) return "image"
    return "document"
}


const ResourceInput = observer( ( { disabled, uploading, selectedResources, setSelectedResources } ) => {
    const [fetchingLinkMetadata, setFetchingLinkMetadata] = useState( false )
    const [draggingOver, setDraggingOver] = useState( false )
    const [link, setLink] = useState( '' )

    const LiveSessionStore = useLiveSessionStore()

    const mimeTypeToExtension = {
        'application/pdf': 'pdf',
        'application/vnd.openxmlformats-officedocument.presentationml.presentation': 'ppt',
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'docx',
        'text/plain': 'txt',
        'video/mp4': 'mp4'
    }

    const { palette, border, theme, layout } = useTheme()

    const handleLinkChange = ( event ) => {
        setLink( event.target.value )

    }

    const handleFilesInsert = ( e ) => {
        const files = e.target.files
        let selectedResourceList = [], inValidFiles = []
        for ( let i = 0; i < files.length; i++ ) {

            const file = files[i]
            const ext = `.${file.name?.split( '.' ).pop()?.toLowerCase()}`

            if ( file.size > 1024 * 1024 * 100 || !supportedFileFormats.includes( ext ) ) {
                inValidFiles.push( file.name )
                continue
            }

            const resource = {}
            resource.resource = file
            resource.name = file.name.split( "." ).slice( 0, -1 ).join( "" )
            resource.type = getFileType( file )
            resource.size = file.size
            resource.id = `${getFileType( file )}-${i}${Date.now()}`

            selectedResourceList.push( resource )
        }
        setSelectedResources( [...selectedResources, ...selectedResourceList] )
        e.target.value = ''
        if ( inValidFiles.length > 0 ) {
            toast( `Failed to load ${inValidFiles.join( ", " )} due to large size or unsupported format.` )
        }
    }

    const handleFileNameChange = ( index, value ) => {
        let values = [...selectedResources]
        values[index].name = value
        setSelectedResources( values )
    }

    const removeAttachment = ( index ) => {
        if ( LiveSessionStore.getUploadProgressList[selectedResources[index].id]?.status === 'inprogress' && LiveSessionStore.getUploadProgressList[selectedResources[index].id]?.controller ) {
            LiveSessionStore.getUploadProgressList[selectedResources[index].id].controller.abort()
        } else {
            const resources = [...selectedResources]
            resources.splice( index, 1 )
            setSelectedResources( resources )
        }
    }

    const fetchLinkMetadata = async () => {
        try {
            if ( link && link.length > 0 ) {
                setFetchingLinkMetadata( true )
                const { data: { data } } = await api.liveSessions.extractLinksMetadata( { links: link.split( "," ).map( l => l?.trim() ).filter( l => l?.length > 0 && l.match( /[(http(s)?)://(www.)?a-zA-Z0-9@:%._+~#=]{2,256}.[a-z]{2,6}\b([-a-zA-Z0-9@:%_+.~#?&//=]*)/ig ) ) } )
                let selectedResourceList = []
                let errorUrls = []
                let index = 0
                for ( const { meta, success } of data ) {
                    if ( success ) {
                        const resource = {}
                        resource.name = meta["og:title"] || meta["twitter:title"] || meta.title || meta.url
                        resource.type = "link"
                        const linkData = {}
                        linkData.url = meta.url
                        linkData.description = meta.description
                        linkData.title = meta["og:title"] || meta["twitter:title"] || meta.title || meta.url
                        linkData.name = meta.title

                        if ( meta["og:image"] || meta["twitter:image"] )
                            linkData.image = ( meta["og:image"] || meta["twitter:image"] ).split( "," )[0]
                        else if ( meta.favicons && meta.favicons?.length > 0 ) {
                            const allIcons = meta.favicons.sort( ( a, b ) => parseInt( a.sizes ) - parseInt( b.sizes ) )
                            const httpFavicon = allIcons.find( i => i.rel === 'icon' && i.href.match( /(http|https)/ ) && ( ["230x230", "228x228", "192x192", "96x96", "32x32", "16x16"].includes( i.sizes ) ) )
                            if ( httpFavicon ) {
                                linkData.image = httpFavicon?.href
                            } else {
                                const favicon = allIcons.find( i => i.rel === 'icon' && ( ["230x230", "228x228", "192x192", "96x96", "32x32", "16x16"].includes( i.sizes ) ) )
                                if ( favicon ) {
                                    const urlOrigin = new URL( link ).origin
                                    linkData.image = `${urlOrigin}${favicon.href}`
                                }
                            }
                        }
                        linkData.description = meta.description || meta.url
                        resource.resource = linkData
                        resource.id = `link-${index}${Date.now()}`
                        selectedResourceList.push( resource )
                    } else {
                        errorUrls.push( meta.url )
                    }
                    index++
                }
                setSelectedResources( [...selectedResources, ...selectedResourceList] )
                setLink( '' )
                if ( errorUrls.length > 0 )
                    toast( "Metadata of these URLs not found: " + errorUrls.join( ", " ) )
            }
        } catch ( err ) {
            console.log( err )
            toast( err?.response?.data?.message || "Couldn't fetch links metadata" )
        } finally {
            setFetchingLinkMetadata( false )
        }
    }


    return (
        <Box display="flex" gap={layout.cardsGap} flexDirection="column" width="100%">
            <Box gap="10px" display="flex" sx={{ cursor: ( disabled || uploading ) ? "not-allowed" : "pointer" }} flexDirection="column">
                <Box
                    sx={{
                        pointerEvents: ( disabled || uploading ) ? "none" : "all",
                        cursor: ( disabled || uploading ) ? "not-allowed" : "pointer",
                        opacity: disabled ? 0.8 : 1
                    }}
                    onDragLeave={() => setDraggingOver( false )}
                    onDrop={() => setDraggingOver( false )}
                    onDragEnter={() => setDraggingOver( true )}
                    onDragOver={() => setDraggingOver( true )}
                    id="file-upload-container"
                    minHeight="150px"
                    borderRadius={layout.borderRadius}
                    position="relative"
                    display="flex"
                    flexDirection="column"
                    overflow="hidden"
                    border={`2px dashed ${draggingOver ? palette.text.secondary : palette.borderColor}`}>
                    <Box display="flex" sx={{ background: palette.contentBg }} flexGrow={1} padding="20px" justifyContent="center" flexDirection="column" gap="10px" alignItems="center">
                        <Box component="input" disabled={uploading || disabled} multiple type="file" onChange={handleFilesInsert} style={{ background: "none", position: "absolute", cursor: "pointer", opacity: "0", top: "0", left: "0", width: "100%", height: "100%" }} />
                        {/* {!draggingOver && <Box component={'img'} id='file-img' width="120px" src={addFileImg} alt="document" />} */}
                        {!draggingOver && <Typography color='textSecondary' display="flex" height="100%" alignItems="center" fontSize="48px">{Icons.default.CloudUpload}</Typography>}
                        {!draggingOver && <Box display="flex" flexDirection="column" gap="5px" alignItems="center">
                            <Typography textAlign="center" display="block" variant='subtitle2'>Drag and drop your files or <Typography display="inline-block" fontWeight="bold" color="customThemeColor.main">browse files</Typography> </Typography>
                            <Typography textAlign="center" display="block" fontSize="14px" color="textSecondary">Supports: <i>{supportedFileFormats.join( ", " )}</i> </Typography>
                            <CustomChip value={`Maximum Size: 100MB`} />
                        </Box>}
                        {draggingOver && <Box display="flex" flexDirection="column" gap="5px" alignItems="center">
                            <Typography sx={{ pointerEvents: "none" }} className='animation-shake' fontWeight="600" variant='body1'>Drop your files here!</Typography>
                            <Typography variant="subtitle2" color="textSecondary" fontSize="12px">Supported formats: {supportedFileFormats.join( ", " )}</Typography>
                            <CustomChip value={`Maximum Size: 100MB`} />
                        </Box>}
                    </Box>
                </Box>
                <Box sx={{ display: 'flex', gap: '10px' }}>
                    <TextField
                        disabled={fetchingLinkMetadata}
                        size='medium'
                        placeholder='Type or paste urls here...'
                        variant="outlined"
                        value={link}
                        sx={{
                            flexGrow: 1,
                            '& input': {
                                outline: link && link?.trim() !== "" && link.split( "," ).map( l => l?.trim() ).filter( l => !( l.match( /([(http(s)?)://(www.)?a-zA-Z0-9@:%._+~#=]{2,256}.[a-z]{2,6}\b([-a-zA-Z0-9@:%_+.~#?&//=]*))*/ig ) ) ).length > 0 ? `2px solid ${palette.errorMessage.main} !important` : "none",
                                borderRadius: `5px`
                            }
                        }}
                        onChange={handleLinkChange} />
                    <Button disabled={fetchingLinkMetadata || uploading} startIcon={fetchingLinkMetadata && <CircularProgress size={14} />} onClick={fetchLinkMetadata} variant='contained' sx={{ paddingX: "40px" }} disableElevation>{fetchingLinkMetadata ? "Fetching details" : "Add"}</Button>
                </Box>
            </Box>
            {selectedResources.length > 0 && <Divider></Divider>}
            <Collapse in={selectedResources.length > 0}  >
                <Box>
                    <Box display="flex" justifyContent="space-between">
                        <Typography gutterBottom display="block" variant='subtitle2' fontSize="14px">Resources selected ({selectedResources.length})</Typography>
                        {/* <Button disabled={uploading} disableElevation size='small' variant='contained' onClick={uploadResources} startIcon={<FileUploadIcon fontSize='small' />} >{uploading ? "Uploading resources..." : "Upload"}</Button> */}
                    </Box>
                    <Box gap="10px" gridTemplateColumns="repeat(auto-fit,minmax(220px,1fr))" display={{ md: "grid", xs: "flex" }} alignItems="center" flexWrap="wrap"  >
                        {selectedResources.length > 0 && selectedResources.map( ( resource, index ) => {
                            const attachment = resource.resource
                            const ext = attachment?.name?.split( "." ).pop()
                            const type = resource.type


                            return (
                                <Card elevation={0} key={index} sx={{ flexGrow: { md: "initial", xs: "1" }, position: "relative", bgcolor: theme === 'dark' && palette.contentBg, border: theme === 'dark' && border[1] }}>
                                    <CardContent sx={{ padding: "0", "&:last-child": { padding: "0" } }}  >
                                        <Box>
                                            <Box display="flex" borderBottom={border[1]} gap="10px" padding="2px 5px" alignItems="center" justifyContent="space-between">
                                                <Typography component="span" fontSize="10px" fontWeight="500" >{type}</Typography>
                                                <Tooltip sx={{ border: border[1] }} title={`Remove ${attachment.name}`}>
                                                    <IconButton color='error' disabled={uploading} onClick={() => { removeAttachment( index ) }} size='small'  >
                                                        <Close sx={{ fontSize: "10px" }} />
                                                    </IconButton>
                                                </Tooltip>
                                            </Box>
                                            <Box display="flex" padding="5px" alignItems="center" gap="10px">
                                                {type === 'link' && (
                                                    attachment['image'] ? < Box component="img" sx={{ background: palette.contentBg, objectFit: "cover", width: "50px", aspectRatio: "1 / 1", }} src={attachment['image']} alt={attachment.name} /> : Icons.default.Link )}
                                                {type !== 'link' && < Box component="img" sx={{ background: palette.contentBg, objectFit: "cover", width: "50px", aspectRatio: "1 / 1", }} src={attachmentThumbnails[ext] ? attachmentThumbnails[ext] : attachmentThumbnails["default"]} alt={attachment.name} />}
                                                <Box width="100%" overflow="hidden">
                                                    <TextField sx={{ flexGrow: 1 }} fullWidth disabled={uploading} onFocus={e => e.target.select()} size="small" placeholder='Enter file name here...' onChange={( e ) => handleFileNameChange( index, e.target.value )} value={resource.name} variant='outlined' slotProps={{
                                                        htmlInput: { sx: { fontSize: "12px" } },
                                                        input: {
                                                            sx: {
                                                                "&.Mui-focused": {
                                                                    boxShadow: "none", // disabing glow effect outside
                                                                    border: border[1],
                                                                    padding: "0"
                                                                },
                                                            }
                                                        }
                                                    }} />
                                                    {/* <Typography variant="subtitle2" display="block" fontSize="10px" color="textSecondary">{type === 'link' ? "URL" : `.${mimeTypeToExtension[attachment.type] || attachment.name?.split( "." ).pop() || 'Unknown Type'}`}</Typography> */}
                                                    <Typography noWrap variant="subtitle2" display="block" fontSize="10px" color="textSecondary">{type === 'link' ? resource.resource.url : resource.resource.name}</Typography>
                                                </Box>
                                            </Box>
                                            <Box>
                                                <Box display="flex" marginRight="15px" margin="5px" gap="10px" alignItems="center" >
                                                    <Typography
                                                        fontSize="10px"
                                                        textTransform="uppercase"
                                                        display="block"
                                                        variant='subtitle2'
                                                        color={
                                                            ['failed', 'cancelled'].includes( LiveSessionStore.getUploadProgressList[resource.id]?.status ) ? "errorMessage.main" :
                                                                LiveSessionStore.getUploadProgressList[resource.id]?.status === 'completed' ? "success" : 'GrayText'
                                                        }
                                                    >
                                                        {LiveSessionStore.getUploadProgressList[resource.id]?.status || "queued"} {LiveSessionStore.getUploadProgressList[resource.id]?.status === 'inprogress' ? `(${Math.ceil( ( LiveSessionStore.getUploadProgressList[resource.id]?.progress || 0 ) * 100 )}%)` : ""}
                                                    </Typography>
                                                    {type !== 'link' && <Typography display="inline" fontSize="10px" color='GrayText'>{getFileSizeFromBytes( attachment.size || 0 )}</Typography>}
                                                </Box>
                                            </Box>
                                        </Box>
                                    </CardContent>
                                    {!["failed", "queued"].includes( LiveSessionStore.getUploadProgressList[resource.id]?.status ) && <Box height="3px" borderRadius="3px" overflow="hidden" width="100%" bgcolor={palette.contentBg}>
                                        <Box width={`${( LiveSessionStore.getUploadProgressList[resource.id]?.progress || 0 ) * 100}%`}
                                            bgcolor={
                                                ['failed', 'cancelled'].includes( LiveSessionStore.getUploadProgressList[resource.id]?.status ) ? palette.errorMessage.main :
                                                    LiveSessionStore.getUploadProgressList[resource.id]?.status === 'completed' ? palette.success.main : palette.primary.main} sx={{ transition: "400ms ease" }} height="100%"></Box>
                                    </Box>}
                                </Card>
                            )
                        } )}
                    </Box>
                </Box>


            </Collapse>
        </Box>
    )
} )

export default ResourceInput